/*
 * File: meta-parser2.ts
 * File Created: 2024-01-10 02:01:20
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2025-12-07 14:32:31
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

/// Parsed Data for Single Attribute
export interface IMetaAttribute {
    /// property name
    name: string;
    /// property name include decendant name, ex: data.name
    fullname: string;
    /// the full text of the property, include comments
    text: string;
    /// the data from type definition
    type: {
        /// type name. ex: string
        name: string;
        /// data related to the type. for enum it's Record,
        /// for another interface it's MetaParser.
        data?: Record<string, string | number> | MetaParser;
        /// optional (?) from the type
        optional: boolean;
        /// array ([]) from the type
        isArray: boolean;
    };
    /// original location of this meta (zero-based)
    index: number;
    /// the tag value can be null
    tags: Array<[string, string | null]>;
    tagMap: Record<string, string | null>;
}

export class MetaAttribute {
    private readonly attribute: IMetaAttribute;

    constructor(attribute: IMetaAttribute) {
        this.attribute = attribute;
    }

    get attr() {
        return this.attribute;
    }
}

/// Parse the interface string into sturctural result
export class MetaParser {
    /// Array and Map
    readonly attributes: MetaAttribute[] = [];
    readonly attributeMap: Record<string, MetaAttribute> = {};
    /// other Metas
    otherMetaMap: Record<string, MetaParser> = {};

    /// ctor
    /// case "undefined" is used for not only no interface, but also no keys.
    /// case "string" is used for normal interface parsing
    /// case "array" is used for row data fallback
    // the name of interface.
    private name?: string;
    // the provided interface itself.
    private interface?: string;
    constructor();
    constructor(keys: string[], otherMetaMap?: MetaParser["otherMetaMap"]);
    constructor(inf: string, otherMetaMap?: MetaParser["otherMetaMap"]);
    constructor(
        infOrKeys?: string | string[],
        otherMetaMap?: MetaParser["otherMetaMap"],
    ) {
        if (infOrKeys == undefined) return;

        if (otherMetaMap) {
            this.otherMetaMap = Object.assign({}, otherMetaMap, this.otherMetaMap);
        }

        if (typeof infOrKeys === "string") {
            /// interface case
            const inf = infOrKeys;
            this.interface = inf;
            const infs = getAllInterfaces(inf);
            // failed to parse interface
            if (infs.length === 0) return;

            const metaParsers: MetaParser[] = [];
            for (let i = 0; i < infs.length; i++) {
                const current = infs[i];
                if (i === 0) {
                    this.name = current.name;
                    this.parseInterfaceInnerString(current.inner);
                    this.otherMetaMap[current.name] = this;
                    metaParsers.push(this);
                } else {
                    const meta = new MetaParser(current.content);
                    this.otherMetaMap[current.name] = meta;
                    metaParsers.push(meta);
                }
            }
            /// handle relations
            for (let metaParser of metaParsers) {
                metaParser.otherMetaMap = this.otherMetaMap;
                /// assign `type.data` if type name matches `otherMetas` type name.
                for (let attribute of metaParser.attributes) {
                    const typename = attribute.attr.type.name;
                    const relatedMeta = this.otherMetaMap[typename];
                    if (relatedMeta == null) continue;
                    /// hook back the relations
                    attribute.attr.type.data = relatedMeta;
                }
            }
        } else if (Array.isArray(infOrKeys)) {
            /// keys case. which used for auto-meta from rows.
            const keys = infOrKeys;
            for (let name of keys) {
                const result = makeDefaultMetaAttribute();
                result.name = result.fullname = name;
                /// default to string
                result.type.name = "string";

                this.pushResult(result);
            }
        }
    }

    /// get meta
    getMetaByIndex(index: number): MetaAttribute | null {
        return this.attributes.length <= index ? null : this.attributes[index];
    }
    getIndexByName(name: string): number {
        return this.attributes.findIndex((value) => value.attr.name === name);
    }

    /// private helpers
    // extract the whole `interface { ... }`
    private getInterfaceByName(meta: string, name?: string): string | null {
        !name && (name = "[^{]*");
        let minfstart = meta.match(new RegExp(`interface\\s+${name}(?:<[^>]+>)?\\s*{`));
        if (!minfstart) return null;
        let infstart = (minfstart.index || 0) + minfstart[0].length;
        let infend: number | undefined;
        let prec = 0;
        let incomment = false;
        for (var i = infstart; i < meta.length; ++i) {
            let cmeta = meta[i];
            if (cmeta === "/" && meta[i + 1] === "*") incomment = true;
            else if (cmeta === "*" && meta[i + 1] === "/") incomment = false;
            else if (!incomment && cmeta === "{") ++prec;
            else if (!incomment && cmeta === "}") {
                --prec;
                if (prec >= 0) continue;
                infend = ++i;
                break;
            }
        }
        if (infend == null) return null;
        /// include array token []
        if (meta.length > infend + 1 && meta[infend] === "[" && meta[infend + 1] === "]")
            infend += 2;
        return meta.substring(infstart, infend);
    }

    // parse interface into results
    private parseInterfaceInnerString(interfaceInnerString: string) {
        let o = interfaceInnerString;
        let result: IMetaAttribute | undefined;

        let comment: string = "";
        const resolveComment = (block: string) => {
            if (result == null) return "";
            const { tags, tagMap } = result;
            if (tags == null || tagMap == null) return "";

            /// ignore this line if the comment is commented out.
            const commented = /^\/{2,}\s+\//i;

            // 解析用：把各種註解前綴拿掉，但不去動原本 comment 字串
            const normalizeLine = (line: string) => {
                let s = line;

                // skip commented-out meta line like "//// @xxx"
                if (commented.test(s.trimStart())) return null;

                s = s
                    // block comment open
                    .replace(/^\s*\/\*\*?\s?/, "")
                    // star line in block
                    .replace(/^\s*\*\s?/, "")
                    // line comment
                    .replace(/^\s*\/\/\/?\s?/, "")
                    // block comment close
                    .replace(/\*\/\s*$/, "");

                return s;
            };

            let currentTag: string | null = null;
            let buf: string[] = [];

            const commit = () => {
                if (!currentTag) return;

                // 你原本想要的 first-in precedence
                // 用 tagMap 來做最可靠（也最小影響）的判斷
                if (Object.prototype.hasOwnProperty.call(tagMap, currentTag)) {
                    currentTag = null;
                    buf = [];
                    return;
                }

                const raw = buf.join("\n").trim();
                const value = raw.length ? raw : null;

                tagMap[currentTag] = value;
                tags.push([currentTag, value]);

                currentTag = null;
                buf = [];
            };

            const lines = block.split(/\r?\n/);
            // 支援同一行多個 @tag + 保留 multiline
            const tagRegex = /@([a-z0-9_-]+)(?:\s*-\s*([^@]*?))?(?=\s+@|$)/gi;

            for (const line of lines) {
                const cleaned = normalizeLine(line);
                if (cleaned == null) continue;

                const ms = Array.from(cleaned.matchAll(tagRegex));

                if (ms.length) {
                    for (let idx = 0; idx < ms.length; idx++) {
                        const m = ms[idx];

                        // 遇到新 tag 之前，先結算上一個（可能來自上一行的 multiline）
                        commit();

                        const tagName = m[1];
                        const hasInlineValue = m[2] !== undefined; // 有出現 `-` 就視為 value tag

                        if (!hasInlineValue) {
                            // flag tag: @uiDisabled @uiSubmitIgnore
                            if (!Object.prototype.hasOwnProperty.call(tagMap, tagName)) {
                                tagMap[tagName] = null;
                                tags.push([tagName, null]);
                            }

                            currentTag = null;
                            buf = [];
                            continue;
                        }

                        // value tag: @uiAttrs - ...
                        currentTag = tagName;
                        buf = [];

                        const first = m[2] ?? "";
                        if (first.length) buf.push(first);

                        // 不是該行最後一個 tag 的話，表示它不可能跨行延伸
                        const isLast = idx === ms.length - 1;
                        if (!isLast) {
                            commit();
                        }
                    }

                    // 這一行已經處理完 tag，直接進下一行
                    continue;
                }

                // 沒有 tag 的行：如果正在 multiline，就視為 continuation
                if (currentTag) {
                    buf.push(cleaned);
                }
            }

            commit();
        };

        /// take properties one by one
        for (let i = 0; i < o.length; ++i) {
            if (result == null) {
                result = makeDefaultMetaAttribute();
                result.text = o;
            }
            /// 1) match `/*` or alphabet
            let letter = o[i];
            if (letter === "/") {
                /// 1.1) matched `/`
                ++i;
                if (o[i] === "*") {
                    /// 1.2) take until `*/`
                    let startpoint = ++i;
                    for (; i < o.length; ++i) {
                        letter = o[i];
                        if (letter === "*" && o[i + 1] === "/") {
                            /// 1.3) matched. take out comment
                            let cmt = o.substring(startpoint - 2, i + 4);
                            comment += cmt;
                        }
                    }
                } else if (o[i] === "/") {
                    /// 1.2) take whole line
                    let startpoint = ++i;
                    for (; i < o.length; ++i) {
                        letter = o[i];
                        if (letter === "\n") {
                            /// 1.3) matched. take out comment
                            let cmt = o.substring(startpoint - 2, i + 2);
                            comment += cmt;
                            break;
                        }
                    }
                } else continue;
            } else if (letter === "_" || letter.toLowerCase() !== letter.toUpperCase()) {
                /// 2) matched alphabet or _ as property start
                let declarationStart = i;
                let declaration = o.substring(i, o.length);

                /// new: match type with ;
                let matches = declaration.match(
                    /["']?([a-z0-9_\-]+)["']?(\?)?[\s\t]*:[\s\t]*/i,
                );
                /// todo: make fullname extends its decendant name.
                result.name = result.fullname = matches![1];
                result.type.optional = matches![2] ? true : false;
                i += matches![0].length;
                let startpoint = i;
                let bracets = 0;
                let incomment = false;
                for (; i < o.length; ++i) {
                    letter = o[i];
                    if (letter === "/" && o[i + 1] === "*") {
                        incomment = true;
                    } else if (letter === "*" && o[i + 1] === "/") {
                        incomment = false;
                    } else if (!incomment && letter === "{") {
                        bracets++;
                    } else if (!incomment && letter === "}") {
                        bracets--;
                    } else if (!incomment && bracets === 0 && letter === ";") {
                        let type = o.substring(startpoint, i);

                        let metaEnd: number = i + 3;
                        resolveComment(comment);
                        result!.text = `${
                            comment + o.substring(declarationStart, metaEnd)
                        }`;
                        /// reset commentStart
                        comment = "";

                        /// parse interface in interface
                        /// deprecated: no inline interface now.
                        if (/^interface /.test(type)) {
                            result.type.name = "interface";
                            result.type.data = new MetaParser(type);
                            let regex = /\[\]$/;
                            result.type.isArray = regex.test(type);
                        } else {
                            /// parse array
                            let regex = /([^[]+)(\[\])?$/;
                            let matches = type.match(regex);
                            if (matches == null) continue;
                            matches[2] && (result.type.isArray = true);
                            result.type.name = matches[1];
                        }
                        break;
                    }
                }

                result.index = this.attributes.length;
                this.pushResult(result as any);

                /// going to make a new one in the next loop
                result = undefined;
            }
        }
    }

    private pushResult(result: IMetaAttribute) {
        /// resolve map
        let name = result.name;

        // /// the first take precedence
        // if (this.attributeMap[name] != undefined) return;

        /// there should not be any duplicate interface property
        if (this.attributeMap[name] != undefined) {
            console.error(`<MetaParser> duplicate property <${result.name}>.`, result);
            return;
        }

        /// add into
        const instance = new MetaAttribute(result);
        this.attributeMap[name] = instance;
        this.attributes.push(instance);
    }
}

/// private helpers
function makeDefaultMetaAttribute(): IMetaAttribute {
    /// make default value
    return {
        name: "",
        fullname: "",
        text: "",
        type: {
            name: "",
            data: undefined,
            optional: true,
            isArray: false,
        },
        index: 0,
        tags: [],
        tagMap: {},
    };
}

function getAllInterfaces(fileContent: string) {
    const regex = /(?<export>export\s+(default\s+)?)?interface\s+(?<name>\w+)/g;

    // 儲存所有 interface 的結果
    const interfaces: { name: string; content: string; inner: string }[] = [];
    let start = -1; // interface 開始位置
    let innerStart = -1; // 大括號的內容開始位置
    let braceCount = 0; // 追蹤大括號的配對狀態
    const matches = Array.from(fileContent.matchAll(regex))
        .sort((a, b) => {
            const aDef = a.groups?.export ? 1 : 0;
            const bDef = b.groups?.export ? 1 : 0;
            if (aDef !== bDef) {
                return bDef - aDef;
            }
            return a.index - b.index;
        })
        .map((o) => ({ name: o.groups?.name, match: o }));
    // const matches = fileContent.matchAll(regex);

    for (const o of matches) {
        /// if match[1] has value, it is `export default`.
        start = o.match.index;
        innerStart = -1;

        // 遍歷整個檔案內容
        for (let i = start; i < fileContent.length; i++) {
            const char = fileContent[i];

            // 追蹤大括號
            if (char === "{") {
                if (innerStart === -1) innerStart = i + 1;
                braceCount++;
            } else if (char === "}") {
                braceCount--;
                // 當所有大括號配對後，記錄 interface 內容
                if (braceCount === 0) {
                    interfaces.push({
                        name: o.name!,
                        content: fileContent.slice(start, i + 1).trim(),
                        inner: fileContent.slice(innerStart, i).trim(),
                    });
                    break;
                }
            }
        }
    }
    return interfaces;
}
